import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import axios from '../../api/axios';
import AdminLayout from '../../layouts/AdminLayout';
import { FaArrowLeft, FaEnvelope, FaPhone, FaMapMarkerAlt, FaBuilding, FaFileInvoice } from 'react-icons/fa';

const DealerDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const [dealer, setDealer] = useState(null);
    const [orders, setOrders] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [dealerRes, ordersRes] = await Promise.all([
                    axios.get(`/users/dealers/${id}`),
                    axios.get(`/orders/user/${id}`)
                ]);
                setDealer(dealerRes.data);
                setOrders(ordersRes.data);
            } catch (error) {
                console.error('Veriler yüklenemedi', error);
            }
        };
        fetchData();
    }, [id]);

    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending': return <span className="badge bg-warning text-dark">{t('my_orders.status_pending')}</span>;
            case 'approved': return <span className="badge bg-info text-dark">{t('my_orders.status_approved')}</span>;
            case 'preparing': return <span className="badge bg-primary">{t('my_orders.status_preparing')}</span>;
            case 'shipped': return <span className="badge bg-secondary">{t('my_orders.status_shipped')}</span>;
            case 'delivered': return <span className="badge bg-success">{t('my_orders.status_delivered')}</span>;
            case 'cancelled': return <span className="badge bg-danger">{t('my_orders.status_cancelled')}</span>;
            default: return <span className="badge bg-light text-dark">{status}</span>;
        }
    };

    if (!dealer) return <AdminLayout><div className="p-5 text-center">{t('common.loading')}</div></AdminLayout>;

    return (
        <AdminLayout>
            <div className="d-flex align-items-center mb-4">
                <Link to="/admin/dealers" className="btn btn-outline-secondary me-3">
                    <FaArrowLeft /> {t('order_detail.back')}
                </Link>
                <h2 className="m-0">{t('dealer_detail.title')} {dealer.company_name}</h2>
            </div>

            <div className="row">
                {/* Sol Taraf: Bayi Bilgileri */}
                <div className="col-md-4 mb-4">
                    <div className="card shadow-sm h-100">
                        <div className="card-header bg-light fw-bold">{t('dealer_detail.firm_info')}</div>
                        <div className="card-body">
                            <div className="mb-3">
                                <label className="text-muted small d-block">{t('dealer_detail.authorized')}</label>
                                <div className="d-flex align-items-center">
                                    <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{width: '32px', height: '32px'}}>
                                        {dealer.username.charAt(0).toUpperCase()}
                                    </div>
                                    <span className="fw-medium">{dealer.username}</span>
                                </div>
                            </div>
                            <div className="mb-3">
                                <label className="text-muted small d-block">{t('dealer_detail.contact')}</label>
                                <div><FaEnvelope className="text-muted me-2" /> {dealer.email}</div>
                                <div><FaPhone className="text-muted me-2" /> {dealer.phone}</div>
                            </div>
                            <div className="mb-3">
                                <label className="text-muted small d-block">{t('dealer_detail.tax_info')}</label>
                                <div><FaFileInvoice className="text-muted me-2" /> {dealer.tax_number}</div>
                                <div className="ms-4 small text-muted">{dealer.tax_office}</div>
                            </div>
                            <div className="mb-3">
                                <label className="text-muted small d-block">{t('dealer_detail.address')}</label>
                                <div className="d-flex">
                                    <FaMapMarkerAlt className="text-muted me-2 mt-1" />
                                    <div>
                                        {dealer.address}<br/>
                                        {dealer.postal_code} {dealer.city} / {dealer.country}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-4 pt-3 border-top">
                                <span className={`badge ${dealer.is_active ? 'bg-success' : 'bg-warning text-dark'} w-100 py-2`}>
                                    {dealer.is_active ? t('dealer_detail.account_active') : t('dealer_detail.pending_approval')}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sağ Taraf: Sipariş Geçmişi */}
                <div className="col-md-8 mb-4">
                    <div className="card shadow-sm h-100">
                        <div className="card-header bg-light fw-bold d-flex justify-content-between align-items-center">
                            <span>{t('dealer_detail.order_history')}</span>
                            <span className="badge bg-secondary">{orders.length} {t('dealer_detail.order_count')}</span>
                        </div>
                        <div className="table-responsive">
                            <table className="table table-hover mb-0 align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th>{t('dashboard.order_no')}</th>
                                        <th>{t('dashboard.date')}</th>
                                        <th>{t('dashboard.amount')}</th>
                                        <th>{t('dashboard.status')}</th>
                                        <th>{t('dashboard.detail')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders.map(order => (
                                        <tr key={order.id}>
                                            <td className="fw-bold">{order.order_number}</td>
                                            <td>{new Date(order.created_at).toLocaleDateString('tr-TR')}</td>
                                            <td>{Number(order.final_amount).toLocaleString('tr-TR', { style: 'currency', currency: 'EUR' })}</td>
                                            <td>{getStatusBadge(order.status)}</td>
                                            <td>
                                                <Link to={`/orders/${order.id}`} className="btn btn-sm btn-outline-primary">
                                                    {t('dashboard.inspect')}
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                    {orders.length === 0 && (
                                        <tr><td colSpan="5" className="text-center p-4 text-muted">{t('dealer_detail.no_orders')}</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default DealerDetail;
